// ─── Librerías externas ──────────────────────────────────────────────────────
import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

// ─── Componentes / Layouts ───────────────────────────────────────────────────
import RightModalLayout from '../../../layouts/Modal/RightModalLayout';
import FormProviderMC from '../../../components/FormProviderMC';
import CustomerInfo from '../components/form/CustomerInfo';
import OrderCustomFields from '../components/form/custom-fields/OrderCustomFields';
import DeliveryMethod from '../components/form/delivery-method/DeliveryMethod';
import OrderInfo from '../components/form/order-info/OrderInfo';
import OrderItems from '../components/form/items/OrderItems';
import OrderSuccessSection from '../components/form/success/OrderSuccessSection';

// ─── Utils / Services / Models / Redux ────────────────────────────────────────
import { useOrderForm } from '../hooks/useOrderForm';
import LoadingMC from '../../../components/LoadingMC';

const OrderModal = memo(({
  customer = {},
  channel,
  createOrderMessage,
  selectedOrder = {},
  platform,
  action,
  onSendPaymentLink,
  onSendPdf
}) => {
  const { t: tCommon } = useTranslation('Common');
  const { t } = useTranslation('Orders');

  // Memoizar los props para evitar re-instanciación del hook
  const useOrderFormProps = useMemo(() => ({
    customer,
    selectedOrder,
    platform,
    action,
    createOrderMessage,
    t,
    tCommon,
    useForm
  }), [customer, selectedOrder, platform, action, createOrderMessage, t, tCommon]);

  const {
    currentStep,
    savingOrderLoading,
    orderSaved,
    saveAction,
    methods,
    getTitle,
    getCaption,
    getSubmitLabel,
    onSubmit,
    hadleBackAction,
    isLoadingInitialData,
    closeModal
  } = useOrderForm(useOrderFormProps);

  if (isLoadingInitialData) {
    return (
      <div className="tw-wrapper">
        <RightModalLayout
          title={getTitle()}
          caption={getCaption()}
        >
          <div className="tw-flex tw-items-center tw-justify-center tw-h-64">
            <div className="tw-text-center">
              <div className="tw-px-6 tw-py-8 tw-text-center tw-w-full">
                <LoadingMC loading={isLoadingInitialData} />
              </div>
            </div>
          </div>
        </RightModalLayout>
      </div>
    );
  }

  return (
    <div className="tw-wrapper">
      <RightModalLayout
        title={getTitle()}
        caption={getCaption()}
        submitLabel={getSubmitLabel()}
        submitAction={currentStep !== 4 ? onSubmit : null}
        cancelLabel={tCommon('actions.back')}
        cancelAction={currentStep !== 4 ? hadleBackAction : null}
        submitDisabled={savingOrderLoading}
        closeAction={closeModal}
        overlayZIndex={300}
        contentZIndex={300}
      >
        <FormProviderMC
          methods={methods}
          className="tw-space-y-4"
        >
          {currentStep === 1 && (
            <>
              <CustomerInfo
                customer={customer}
              />
              <OrderCustomFields
                order={selectedOrder}
              />
              <DeliveryMethod
                customer={customer}
              />
            </>
          )}
          {currentStep === 2 && (
            <OrderInfo
              channel={channel}
              action={action}
            />
          )}
          {currentStep === 3 && (
            <OrderItems />
          )}
          {currentStep === 4 && (
            <OrderSuccessSection
              order={orderSaved}
              action={saveAction}
              platform={channel || platform}
              onSendPaymentLink={onSendPaymentLink}
              onSendPdf={onSendPdf}
              closeParentModal={closeModal}
            />
          )}
        </FormProviderMC>
      </RightModalLayout>
    </div>
  );
});

export default OrderModal;
